// useTreeKeyboardNav — arrow-key navigation across the laid-out org tree.
//
// Wraps a `HierarchyPointNode` layout (from `useTreeLayout`) plus the
// currently-focused id and returns:
//   * `focusedNodeId` — current focus target (drives `tabIndex=0` on that
//     card and `data-focused="true"` so tests can assert it).
//   * `setFocusedNodeId` — used by `onFocus` handlers when a card receives
//     real DOM focus (e.g. Tab-into).
//   * `handleKeyDown` — wired onto each TreeNode `<g>`. Returns `true` when
//     it consumed the event so the caller can `preventDefault` upstream if
//     needed. Currently it preventDefaults itself.
//
// Adjacency rules (Up/Down ascend/descend the layout, Left/Right walk
// sibling lists sorted by displayName):
//   * `ArrowUp`   → manager id (the layout node's `parent.data.id`).
//   * `ArrowDown` → first visible direct report
//                   (`children[0].data.id`, alphabetically by displayName).
//   * `ArrowLeft` / `ArrowRight` → previous / next sibling sorted by
//                                  `displayName.localeCompare(...)`.
//
// Space toggles the focused card's collapse state via the supplied
// `onToggle` callback. Enter is intentionally NOT handled here — the
// TreeNode's own onSelect (drawer-open) takes priority on Enter, matching
// US4. The caller passes `onToggle` so we can keep the hook agnostic of the
// useTreeCollapse hook (DI for testing).
//
// Cross-runtime focus restore: when the focused id changes we move actual
// DOM focus to the corresponding `<g>` (looked up by its `data-upn`
// attribute). The pattern mirrors `org-graph/OrgGraph.tsx`'s
// `findAdjacentNode` keyboard handler: prefer `CSS.escape` then fall back to
// attribute iteration on older runtimes / jsdom builds where `CSS.escape`
// is missing.

import { useCallback, useMemo, type KeyboardEvent, type RefObject } from 'react';
import type { HierarchyPointNode } from 'd3-hierarchy';
import type { IOrgGraphNode } from '@skystone/shared';
import { escapeAttributeValue, findByDataUpn } from '@/lib/svg-focus';
import { VIRTUAL_ROOT_ID } from './useTreeLayout';

/** One row in the adjacency index: where Up/Down/Left/Right should land. */
interface TreeAdjacency {
  managerId: string | null;
  /** First visible direct report id (alphabetically), or null if leaf. */
  firstChildId: string | null;
  /** All siblings (INCLUDING self), sorted alphabetically by displayName.
   *  Storing the full list lets us locate the current position via
   *  `indexOf(focusedId)` without comparing display names. */
  siblingsSorted: string[];
}

export interface UseTreeKeyboardNavOptions {
  /** Laid-out tree root, or `null` if there are no renderable nodes. */
  layout: HierarchyPointNode<IOrgGraphNode> | null;
  /** Set to `true` when `layout` has a synthetic root injected. We then
   *  skip the virtual node when traversing parents so Up never targets it. */
  hasVirtualRoot: boolean;
  /** Currently focused node id; `null` when no tree node has focus. */
  focusedNodeId: string | null;
  /** Updates focused state (and indirectly the rendered tabIndex on each
   *  card). */
  setFocusedNodeId: (id: string) => void;
  /** Collapse-toggle invoked when Space is pressed on the focused card. */
  onToggle: (id: string) => void;
  /** SVG root used to locate target nodes for DOM focus. May be null in
   *  jsdom before refs are attached — the hook tolerates that. */
  svgRef: RefObject<SVGSVGElement | null>;
}

export interface UseTreeKeyboardNavResult {
  handleKeyDown: (event: KeyboardEvent<SVGGElement>) => void;
}

/**
 * Locate the focusable `<g data-testid="tree-node" data-upn="...">` under
 * the SVG root, preferring `CSS.escape` but falling back to attribute
 * iteration so older runtimes (and JSDOM without CSSOM) still work.
 */
function findTreeNodeGroup(svg: SVGSVGElement, upn: string): SVGGElement | null {
  const escaped = escapeAttributeValue(upn);
  const element = escaped === null
    ? findByDataUpn(svg, upn)
    : svg.querySelector(`[data-testid="tree-node"][data-upn="${escaped}"]`);
  return (element as SVGGElement | null) ?? null;
}

/**
 * Build the manager / first-child / sibling adjacency for every renderable
 * node in the layout. Siblings are sorted alphabetically by displayName so
 * Left/Right navigation is predictable for users.
 *
 * Skips the synthetic virtual root: its children are real roots, whose
 * sibling list is the set of real roots themselves (sorted by name).
 */
function buildAdjacency(
  layout: HierarchyPointNode<IOrgGraphNode>,
  hasVirtualRoot: boolean,
): Map<string, TreeAdjacency> {
  const adjacency = new Map<string, TreeAdjacency>();
  const allNodes = layout.descendants();

  for (const point of allNodes) {
    if (hasVirtualRoot && point.data.id === VIRTUAL_ROOT_ID) continue;

    // Sibling list: every child of THIS node's parent that isn't the
    // virtual root, sorted by displayName. For real roots whose layout
    // parent is the virtual root, this naturally yields the set of real
    // roots — exactly what Left/Right should walk across at the top level.
    const layoutParent = point.parent;
    const siblingGroup = (layoutParent?.children ?? [point])
      .filter((n) => !(hasVirtualRoot && n.data.id === VIRTUAL_ROOT_ID))
      .map((n) => n.data)
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
    const siblingsSorted = siblingGroup.map((n) => n.id);

    // First visible child = alphabetically-first direct report in the
    // current (post-collapse) layout. d3 already sorted children by their
    // input order during stratification, so we sort again here for the
    // user-facing alphabetical guarantee.
    const childGroup = (point.children ?? [])
      .map((n) => n.data)
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
    const firstChildId = childGroup.length > 0 ? childGroup[0].id : null;

    // Manager id: parent's data.id unless the parent is the virtual root,
    // in which case there is no manager to surface to the user.
    const parentId = layoutParent?.data.id ?? null;
    const managerId =
      parentId !== null && !(hasVirtualRoot && parentId === VIRTUAL_ROOT_ID)
        ? parentId
        : null;

    adjacency.set(point.data.id, {
      managerId,
      firstChildId,
      siblingsSorted,
    });
  }

  return adjacency;
}

/**
 * Resolve the id of the adjacent node for a given direction key. Returns
 * null when no such node exists (e.g. ArrowUp from a root, ArrowDown from
 * a leaf, ArrowLeft/Right when only one sibling).
 */
function findAdjacentId(
  focusedId: string,
  key: string,
  adjacency: ReadonlyMap<string, TreeAdjacency>,
): string | null {
  const entry = adjacency.get(focusedId);
  if (entry === undefined) return null;
  if (key === 'ArrowUp') return entry.managerId;
  if (key === 'ArrowDown') return entry.firstChildId;
  if (key === 'ArrowLeft' || key === 'ArrowRight') {
    const list = entry.siblingsSorted;
    if (list.length <= 1) return null;
    const currentIndex = list.indexOf(focusedId);
    if (currentIndex === -1) return null;
    if (key === 'ArrowLeft') {
      const prevIndex = currentIndex === 0 ? list.length - 1 : currentIndex - 1;
      return list[prevIndex];
    }
    const nextIndex = currentIndex === list.length - 1 ? 0 : currentIndex + 1;
    return list[nextIndex];
  }
  return null;
}

/**
 * Arrow-key navigation + Space-to-toggle behaviour for the org tree.
 *
 * Hand the returned `handleKeyDown` to each TreeNode's `<g onKeyDown>` —
 * the React event already carries the source DOM node, so we route through
 * the `data-upn` attribute on the target to discover which card the user
 * was on.
 */
export function useTreeKeyboardNav(
  options: UseTreeKeyboardNavOptions,
): UseTreeKeyboardNavResult {
  const { layout, hasVirtualRoot, focusedNodeId, setFocusedNodeId, onToggle, svgRef } = options;

  const adjacency = useMemo(() => {
    if (layout === null) return new Map<string, TreeAdjacency>();
    return buildAdjacency(layout, hasVirtualRoot);
  }, [layout, hasVirtualRoot]);

  const moveFocusTo = useCallback(
    (id: string) => {
      setFocusedNodeId(id);
      // Move actual DOM focus so subsequent arrow keys originate from the
      // newly-focused node, not the stale one whose handler ran this event.
      // Deferred to next animation frame so React has flushed tabIndex
      // updates on the rendered cards before we hand focus over.
      if (globalThis.window === undefined) return;
      globalThis.window.requestAnimationFrame(() => {
        const root = svgRef.current;
        if (root === null) return;
        const group = findTreeNodeGroup(root, id);
        if (group !== null && typeof group.focus === 'function') {
          group.focus();
        }
      });
    },
    [setFocusedNodeId, svgRef],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<SVGGElement>) => {
      if (focusedNodeId === null) return;

      // Space: toggle collapse on the focused card. Enter is left alone so
      // the TreeNode's existing onSelect (drawer-open) keeps working.
      if (event.key === ' ') {
        event.preventDefault();
        onToggle(focusedNodeId);
        return;
      }

      const targetId = findAdjacentId(focusedNodeId, event.key, adjacency);
      if (targetId === null) return;
      event.preventDefault();
      moveFocusTo(targetId);
    },
    [focusedNodeId, adjacency, onToggle, moveFocusTo],
  );

  return { handleKeyDown };
}

// Exposed for direct unit tests — the adjacency helpers above are pure and
// most of the hook's behaviour is captured by exercising them.
// `findTreeNodeGroup` is also exposed so the CSS.escape-present /
// CSS.escape-missing branches around the data-upn lookup can be asserted
// without spinning up the full OrgTree.
export const __testables = {
  buildAdjacency,
  findAdjacentId,
  findTreeNodeGroup,
};
